// src/components/NotificationBadge.js - OPTIMIZED FOR REAL BACKEND (ENGLISH)
import React, { useState, useCallback, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Button,
  // eslint-disable-next-line no-unused-vars
  CircularProgress,
  // eslint-disable-next-line no-unused-vars
  Alert,
  Tooltip,
  IconButton as MuiIconButton,
  Stack,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNotifications } from '../context/NotificationContext';
import { getNotificationRoute } from '../services/nativeNotificationService';

const NOTIFICATION_TYPES = {
  success: { icon: CheckCircleIcon, color: '#10b981', bgColor: '#d1fae5' },
  warning: { icon: WarningIcon, color: '#f59e0b', bgColor: '#fef3c7' },
  info: { icon: InfoIcon, color: '#3b82f6', bgColor: '#dbeafe' },
  error: { icon: ErrorIcon, color: '#ef4444', bgColor: '#fee2e2' },
};

const CATEGORY_COLORS = {
  projects: '#8b5cf6',
  tasks: '#ec4899',
  clients: '#06b6d4',
  users: '#6366f1',
  documents: '#14b8a6',
  finance: '#f59e0b',
  system: '#64748b',
  chat: '#8b5cf6',
  calendar: '#10b981',
};

export default function NotificationBadge() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const {
    notifications = [],
    loading,
    // eslint-disable-next-line no-unused-vars
    error,
    unreadCount = 0,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    fetchNotifications,
  } = useNotifications();

  const [anchorEl, setAnchorEl] = useState(null);

  // Load notifications when popover opens
  useEffect(() => {
    if (anchorEl) {
      fetchNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl]);

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleNotificationClick = useCallback(async (notification) => {
    // Mark as read
    await markAsRead(notification.id);

    navigate(getNotificationRoute(notification));
    handleClose();
  }, [markAsRead, navigate, handleClose]);

  const handleDelete = useCallback(async (e, notificationId) => {
    e.stopPropagation();
    await deleteNotification(notificationId);
  }, [deleteNotification]);

  const handleMarkAllAsRead = useCallback(async () => {
    await markAllAsRead();
  }, [markAllAsRead]);

  const handleClearAll = useCallback(async () => {
    if (window.confirm('Are you sure you want to delete ALL notifications?')) {
      await clearAll();
      handleClose();
    }
  }, [clearAll, handleClose]);

  const handleRefresh = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Notifications" arrow placement="bottom">
        <IconButton
          color="inherit"
          onClick={handleClick}
          aria-label={`${unreadCount} unread notifications`}
          aria-haspopup="dialog"
          aria-expanded={open}
          sx={{
            transition: 'all 0.2s',
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'scale(1.05)',
              },
            },
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none',
                fontWeight: 700,
                fontSize: '0.7rem',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.1)' },
                  '100%': { transform: 'scale(1)' },
                },
              },
            }}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            width: isMobile ? 'calc(100vw - 24px)' : 400,
            maxWidth: isMobile ? 'calc(100vw - 24px)' : 450,
            maxHeight: isMobile ? 'min(72dvh, 620px)' : '600px',
            mt: isMobile ? 1 : 1.5,
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2.5,
            background: 'linear-gradient(135deg, #1976d2 0%, #115293 100%)',
            color: 'white',
            flexShrink: 0,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Notifications
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {unreadCount > 0
                  ? `${unreadCount} new`
                  : 'All caught up'}
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Refresh" arrow>
                <MuiIconButton
                  size="small"
                  onClick={handleRefresh}
                  disabled={loading}
                  aria-label="Refresh notifications"
                  sx={{
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' }
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </MuiIconButton>
              </Tooltip>
              
              {notifications.length > 0 && (
                <>
                  {unreadCount > 0 && (
                    <Tooltip title="Mark all as read" arrow>
                      <MuiIconButton
                        size="small"
                        onClick={handleMarkAllAsRead}
                        aria-label="Mark all notifications as read"
                        sx={{
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' }
                        }}
                      >
                        <DoneAllIcon fontSize="small" />
                      </MuiIconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Clear all" arrow>
                    <MuiIconButton
                      size="small"
                      onClick={handleClearAll}
                      aria-label="Clear all notifications"
                      sx={{
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' }
                      }}
                    >
                      <ClearAllIcon fontSize="small" />
                    </MuiIconButton>
                  </Tooltip>
                </>
              )}
            </Stack>
          </Stack>
        </Box>

        {/* Scrollable Content */}
        <Box 
          sx={{ 
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            minHeight: 0,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '10px',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.3)',
              },
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress size={40} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2, opacity: 0.3 }} />
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                No notifications
              </Typography>
              <Typography variant="caption" color="text.secondary">
                We'll notify you when something arrives
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.slice(0, 10).map((notification, index) => {
                const { icon: Icon, color, bgColor } = NOTIFICATION_TYPES[notification.type] || NOTIFICATION_TYPES.info;
                const isRead = notification.read === true || notification.read === 1;
                const categoryColor = notification.category ? CATEGORY_COLORS[notification.category] : color;

                return (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      alignItems="flex-start"
                      sx={{
                        bgcolor: isRead ? 'background.paper' : `${color}08`,
                        borderLeft: '4px solid',
                        borderLeftColor: isRead ? 'transparent' : color,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        py: { xs: 1.5, sm: 2 },
                        px: { xs: 1.5, sm: 2.5 },
                        '&:hover': {
                          bgcolor: `${color}15`,
                          borderLeftColor: color,
                        },
                      }}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <ListItemAvatar sx={{ minWidth: 48 }}>
                        <Avatar
                          sx={{
                            bgcolor: bgColor,
                            width: 40,
                            height: 40,
                          }}
                        >
                          <Icon sx={{ color, fontSize: 22 }} />
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: isRead ? 'text.secondary' : 'text.primary',
                                flex: 1,
                                minWidth: 0,
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {notification.title}
                            </Typography>
                            {notification.category && (
                              <Chip
                                label={notification.category}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.6rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  bgcolor: `${categoryColor}20`,
                                  color: categoryColor,
                                }}
                              />
                            )}
                          </Stack>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ display: 'block', mb: 0.5, lineHeight: 1.5 }}
                            >
                              {notification.message}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: 'text.disabled', fontWeight: 500 }}
                            >
                              {formatTime(notification.created_at)}
                            </Typography>
                          </Box>
                        }
                      />

                      <Stack direction="column" alignItems="center" spacing={0.5} ml={1}>
                        {!isRead && (
                          <FiberManualRecordIcon sx={{ fontSize: 10, color: color }} />
                        )}
                        <Tooltip title="Delete" arrow>
                          <MuiIconButton
                            size="small"
                            onClick={(e) => handleDelete(e, notification.id)}
                            sx={{
                              p: 0.5,
                              '&:hover': { color: 'error.main' },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </MuiIconButton>
                        </Tooltip>
                      </Stack>
                    </ListItem>
                    {index < Math.min(notifications.length, 10) - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        {/* Footer */}
        {notifications.length > 0 && (
          <Box 
            sx={{ 
              p: 2, 
              borderTop: '1px solid', 
              borderColor: 'divider', 
              bgcolor: 'background.paper', 
              flexShrink: 0,
            }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                handleClose();
                navigate('/notifications');
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                py: 1.2,
                fontSize: isMobile ? '0.9rem' : '1rem',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                }
              }}
            >
              View all notifications
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
}

function formatTime(timestamp) {
  if (!timestamp) return 'Just now';

  const now = new Date();
  const time = new Date(timestamp);
  const diff = Math.floor((now - time) / 1000);

  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;

  return time.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
  });
}
